// @givmo/org-console — the pieces every served-definition card shares (Org Machinery OM-17 seam A;
// OM-64, Design 4C §8): the header, the last-change line and the save-failed line. ONE card shape for
// every definition — a knob, a kept fact, a read-only fact — so these live once.

import type { ReactNode } from 'react';
import { CardHeader, testIdProp } from '@givmo/design-system';
import { formatOrgChangeDay } from './org-decode';
import { GLOBAL_ADMIN_LABEL } from './org-tiers';
import type { OrgSettingDefinition, OrgSettingState } from './wire-types';

export const SETTING_SAVE_FAILED_LINE =
  'We couldn’t save that. Nothing was changed — please try again.';

/**
 * The line a read-only fact says (Design 4C §8; D-CPO-69 question 6): who can change it. OM-61
 * replaces it with Design 4B's refusal and request.
 */
export const FACT_READ_ONLY_LINE = `A ${GLOBAL_ADMIN_LABEL} can change this.`;

/** The value of a fact nobody has entered, where it is drawn as text (Design 4C §8). */
export const FACT_NOT_SET = 'Not set';

/**
 * A person the roster cannot name, mid-sentence (D-CCM-99's words; kept by D-CPO-69 question 12):
 * "Changed by someone in your organization, <date>".
 */
const SOMEONE_MID_SENTENCE = 'someone in your organization';

/**
 * The card's last-change line (design 4A §11 item 6, RULED): "Changed by <name>, <date>". A change
 * made through the person's connected AI says so, in D-CCM-96's naming ("<Name>’s AI", or "a
 * connected AI" where the roster cannot name them). `null` when there is nothing true to say: never
 * changed (revision 0), or no readable day.
 */
export function settingChangeLine(state: OrgSettingState, name: string | undefined): string | null {
  if (state.revision === 0) return null;
  const day = formatOrgChangeDay(state.updated_at);
  if (day.length === 0) return null;
  if (state.channel === 'connector') {
    return `Changed through ${name !== undefined ? `${name}’s AI` : 'a connected AI'}, ${day}`;
  }
  return `Changed by ${name ?? SOMEONE_MID_SENTENCE}, ${day}`;
}

/** The last-change line for a served state, once the roster can name the person; else `null`. */
export function lastChangeFor(
  state: OrgSettingState | undefined,
  personName: ((userId: string) => string | undefined) | undefined,
): string | null {
  if (state === undefined || personName === undefined) return null;
  return settingChangeLine(
    state,
    state.updated_by_user_id === null ? undefined : personName(state.updated_by_user_id),
  );
}

/**
 * The card's header: the served label as a WRAPPING `h3` (the design system's `CardHeader` title
 * truncates) and the served description. Nothing in the header's actions slot (the mockup pass's
 * pinned requirement 1).
 */
export function DefinitionHeader({
  definition,
  headingId,
}: {
  definition: OrgSettingDefinition;
  headingId: string;
}) {
  return (
    <CardHeader>
      <div className="min-w-0">
        <h3 id={headingId} className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {definition.label}
        </h3>
        {definition.description !== null && (
          <p className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
            {definition.description}
          </p>
        )}
      </div>
    </CardHeader>
  );
}

/**
 * A fact's value drawn as text — its lines, or "Not set" in the card's tertiary text where nobody has
 * entered it.
 */
export function FactValue({ lines, testId }: { lines: string[] | null; testId: string }) {
  return (
    <div
      className="min-w-0 font-sans text-s14 font-w400 leading-h15 tracking-givmo"
      {...testIdProp(testId)}
    >
      {lines === null ? (
        <p className="text-text-tertiary">{FACT_NOT_SET}</p>
      ) : (
        lines.map((line) => (
          <p key={line} className="text-text-strong">
            {line}
          </p>
        ))
      )}
    </div>
  );
}

/** The page's one style for a line under a control (the Landing card's hint). */
export function CardHintLine({ testId, children }: { testId?: string; children: ReactNode }) {
  return (
    <p
      className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
      {...testIdProp(testId)}
    >
      {children}
    </p>
  );
}
